import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Sidebar, type TSettingsMenuSection } from "./Sidebar";
import { TeamTable } from "./TeamTable";
import { Users, User } from "lucide-react";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { ProfileSection } from "./ProfileSection";
import { useSearchParams } from "react-router-dom";
import { hasAdminPrivileges } from "@/zapi-frontend/shared/utils/permissions";
import { BuildingIcon, SlidersHorizontalIcon } from "@phosphor-icons/react";
import { GeneralSection } from "./_GeneralSection";
import { PreferencesSection } from "./_PreferencesSection";

export type TSettingsTab = "Team" | "Profile" | "General" | "Preferences";

export function SettingsPage() {
  const { t } = useTranslation("settings");
  const { role } = useMeStore();
  const isAdmin = hasAdminPrivileges(role);

  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") as TSettingsTab | null;
  const [activeTab, setActiveTab] = useState<TSettingsTab>(initialTab || "Profile");

  const renderContent = useMemo(() => {
    if (activeTab === "Profile") return <ProfileSection />;
    if (activeTab === "General") return <GeneralSection />;
    if (activeTab === "Preferences") return <PreferencesSection />;

    return <TeamTable />;
  }, [activeTab]);

  const menuItems: TSettingsMenuSection[] = [
    {
      key: "Account",
      label: t("groups.account"),
      items: [
        { name: "Profile", label: t("tabs.profile"), icon: <User /> },
        { name: "Preferences", label: t("tabs.preferences"), icon: <SlidersHorizontalIcon /> }
      ]
    },
    ...(isAdmin
      ? [
          {
            key: "Organization",
            label: t("groups.organization"),
            items: [
              { name: "General" as const, label: t("tabs.general"), icon: <BuildingIcon /> },
              { name: "Team" as const, label: t("tabs.team"), icon: <Users /> }
            ]
          }
        ]
      : [])
  ];

  function handleTabChange(tab: TSettingsTab) {
    setActiveTab(tab);
    setSearchParams({ tab });
  }

  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);

  return (
    <div className="mx-auto mt-2 w-[calc(100%-16px)] h-[calc(100vh-var(--app-header-height)-16px)] grid grid-cols-[260px_1fr] bg-white">
      <Sidebar activeTab={activeTab} setActiveTab={handleTabChange} menuItems={menuItems} />
      <main className="overflow-y-auto py-[18px] px-[32px]">{renderContent}</main>
    </div>
  );
}
